import { FlexPreset } from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/types";
import { FLEX_IZ_NO_IGNORE_ZONES_OPTION } from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/flexIzPresetSelectConstants";
import {
  collapseNoIgnoreZonesPresetOptions,
  filterFlexPresetsForApiPresetDropdown,
  isNoIgnorePreset,
} from "../../Sites/SiteAboutPage/FlexIgnoreZonesOnboarding/flexPresetDisplayDedupe";
import { IgnoreZone } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

const normalizeDisplay = (s: string | undefined): string =>
  (s || "").trim().toLowerCase();

/**
 * Map a template row from `GET /camera/{id}/ignore_zones/` to the row in the active
 * preset. Preset-zone payloads often use different `label` strings than the camera
 * ignore_zones list; resolve by label, then Ignore All (`zone_type === 0`), then
 * `metric_id` + `zone_type`, then display name + zone type.
 */
export const findPresetIgnoreZoneRowForTemplate = (
  template: IgnoreZone,
  presetZones: readonly IgnoreZone[]
): IgnoreZone | undefined => {
  const byLabel = presetZones.find((z) => z.label === template.label);
  if (byLabel) {
    return byLabel;
  }
  const tType = Number(template.zone_type);
  if (tType === 0) {
    return presetZones.find((z) => Number(z.zone_type) === 0);
  }
  const tMetric = Number(template.metric_id);
  const byMetricType = presetZones.find(
    (z) => Number(z.zone_type) === tType && Number(z.metric_id) === tMetric
  );
  if (byMetricType) {
    return byMetricType;
  }
  const tName = normalizeDisplay(template.label_display_name);
  if (!tName) {
    return undefined;
  }
  return presetZones.find(
    (z) =>
      Number(z.zone_type) === tType &&
      normalizeDisplay(z.label_display_name) === tName
  );
};

/** Serialized zones + trimmed name for dirty detection (matches PATCH name semantics). */
export const flexPresetZonesAndNameSnapshot = (p: FlexPreset): string =>
  JSON.stringify({
    zones: p.zones || [],
    name: (p.name || "").trim(),
  });

/**
 * Trimmed display name from a baseline snapshot (`flexPresetZonesAndNameSnapshot`).
 * Returns undefined for legacy zones-only JSON (array) or unreadable values — skip
 * inferring `PATCH preset/{id}/` from baseline name diff in that case.
 */
export const trimmedPresetNameFromBaselineSnapshot = (
  raw: string | undefined
): string | undefined => {
  if (raw == null) {
    return undefined;
  }
  try {
    const o = JSON.parse(raw) as unknown;
    if (
      o &&
      typeof o === "object" &&
      !Array.isArray(o) &&
      "name" in o &&
      typeof (o as { name: unknown }).name === "string"
    ) {
      return ((o as { name: string }).name || "").trim();
    }
  } catch {
    /* ignore */
  }
  return undefined;
};

export const computeFlexZonesBaseline = (
  presets: FlexPreset[]
): Record<string, string> => {
  const o: Record<string, string> = {};
  for (const p of presets) {
    if (p.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION) {
      continue;
    }
    o[p.localId] = flexPresetZonesAndNameSnapshot(p);
  }
  return o;
};

/**
 * Preset dropdown options for Edit Ignore Zones only: API presets + drafts; no synthetic
 * `default-*` rows. Unlike Site About / Edit Camera pickers, “No Ignore Zones” is omitted
 * entirely here (synthetic id and persisted API preset with that name).
 */
export const getEditIgnoreZonesFlexPresetMenuPresets = (
  presets: FlexPreset[],
  preferredLocalId?: string
): FlexPreset[] =>
  collapseNoIgnoreZonesPresetOptions(
    filterFlexPresetsForApiPresetDropdown(presets),
    preferredLocalId
  ).filter((p) => !isNoIgnorePreset(p));

export const getDirtyFlexPresetLocalIds = (
  presets: FlexPreset[],
  baseline: Record<string, string>
): string[] => {
  const dirty: string[] = [];
  for (const p of presets) {
    if (p.localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION) {
      continue;
    }
    const cur = flexPresetZonesAndNameSnapshot(p);
    const base = baseline[p.localId];
    if (base !== cur) {
      dirty.push(p.localId);
    }
  }
  return dirty;
};
